import type { JSX } from 'react';
import { euroDecimalToMicroEur } from '@majuscule/domain';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { internalFetch } from '../../lib/api';
import { type Tariff, tariffInEffect } from '../../lib/tariffs';
import type { RuleStatus, UseCase } from '../../lib/use-cases';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Active / inactive badge of a use case (archived = `active === false`, D-UC.2). */
export function UseCaseStatusBadge({ useCase }: { useCase: UseCase }): JSX.Element {
  return useCase.active ? (
    <Badge variant="success">Actif</Badge>
  ) : (
    <Badge variant="neutral">Inactif</Badge>
  );
}

const RULE_STATUS_BADGES: Record<RuleStatus, { variant: BadgeVariant; label: string }> = {
  current: { variant: 'success', label: 'Actif' },
  scheduled: { variant: 'info', label: 'En attente' },
  superseded: { variant: 'neutral', label: 'Expirée' },
};

/** Badge of a pricing-rule version: in effect, dated later, or replaced. */
export function RuleStatusBadge({ status }: { status: RuleStatus }): JSX.Element {
  const { variant, label } = RULE_STATUS_BADGES[status];
  return <Badge variant={variant}>{label}</Badge>;
}

/**
 * A section of the use-case sheet: heading (h3) and description on the left,
 * actions on the right, then its content.
 */
export function SheetSection({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={cn('grid min-w-0 gap-3', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="m-0 text-heading">{title}</h3>
          {description !== undefined ? (
            <p className="m-0 mt-0.5 text-caption text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {actions !== undefined ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Purchase cost of an SMS service: its standard tariff in effect (PRD 8.2). */
export type SmsServiceCost =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'missing' }
  | {
      status: 'ok';
      tariff: Tariff;
      /** Cost of one SMS in micro-euros, when the tariff is in euros; null in dollars. */
      costMicroEur: number | null;
    };

/**
 * Standard tariff in effect of an SMS service, to set its selling price with a
 * margin; `missing` when the service has none (every send would then fail).
 */
export function useSmsServiceCost(modelId: string): SmsServiceCost {
  const tariffs = useQuery({
    queryKey: ['tariffs', modelId],
    queryFn: () => internalFetch<{ tariffs: Tariff[] }>(`/internal/models/${modelId}/tariffs`),
    enabled: modelId.length > 0,
  });
  if (modelId.length === 0) {
    return { status: 'none' };
  }
  if (tariffs.data === undefined) {
    return { status: 'loading' };
  }
  const current = tariffInEffect(tariffs.data.tariffs, 'standard', Date.now());
  if (current === undefined) {
    return { status: 'missing' };
  }
  const micro = current.currency === 'EUR' ? euroDecimalToMicroEur(current.perMessage) : null;
  return {
    status: 'ok',
    tariff: current,
    costMicroEur: micro !== null && micro.ok ? micro.value : null,
  };
}

/** A margin as a share of the selling price: « 19,3 % ». */
export function formatMarginShare(marginMicroEur: number, priceMicroEur: number): string {
  if (priceMicroEur === 0) {
    return '—';
  }
  const share = (marginMicroEur / priceMicroEur) * 100;
  return `${share.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u00a0%`;
}
